import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from '@mui/material/styles';

const EmailVerificationModal = ({ open, onClose, email, onResendEmail, isResending }) => {
  const theme = useTheme();
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          bgcolor: 'rgba(37, 37, 48, 0.98)',
          border: `2px solid ${theme.palette.neon.lime}`,
          boxShadow: `0 0 30px ${theme.palette.neon.lime}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, transparent, ${theme.palette.neon.lime}, transparent)`,
            animation: 'scan 3s linear infinite',
          },
          '@keyframes scan': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          textAlign: 'center', 
          pt: 3,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: theme.palette.neon.lime,
          textShadow: `0 0 20px ${theme.palette.neon.lime}`,
          position: 'relative',
          pr: 6,
        }}
      >
        メール認証が必要です
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.neon.lime,
            '&:hover': {
              transform: 'rotate(90deg)',
              color: theme.palette.neon.pink,
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <EmailIcon 
              sx={{ 
                fontSize: 80, 
                color: theme.palette.neon.lime, 
                mb: 2,
                filter: `drop-shadow(0 0 20px ${theme.palette.neon.lime})`,
              }} 
            />
            <CheckCircleIcon 
              sx={{ 
                position: 'absolute',
                bottom: 10,
                right: -10,
                fontSize: 30,
                color: theme.palette.neon.cyan,
                bgcolor: 'rgba(37, 37, 48, 0.98)',
                borderRadius: '50%',
                filter: `drop-shadow(0 0 10px ${theme.palette.neon.cyan})`,
              }}
            />
          </Box>
          
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{
              color: theme.palette.neon.lime,
              fontWeight: 700,
              letterSpacing: '0.05em',
            }}
          >
            {email}
          </Typography>
          
          <Typography 
            variant="body1" 
            gutterBottom
            sx={{ 
              mb: 2,
              color: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            宛に確認メールを送信しました
          </Typography>
          
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: 'rgba(0, 255, 0, 0.05)',
              border: `1px solid ${theme.palette.neon.lime}`,
              borderRadius: 0,
              mb: 2,
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: 1.8,
              }}
            >
              メール内のリンクをクリックして、アカウントを有効化してください。
            </Typography>
          </Box>
          
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.875rem',
            }}
          >
            メールが届かない場合は、迷惑メールフォルダをご確認いただくか、
            再送信ボタンをクリックしてください。
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          onClick={onResendEmail}
          disabled={isResending}
          variant="outlined"
          sx={{
            mr: 1,
            borderRadius: 0,
            borderColor: theme.palette.neon.cyan,
            borderWidth: 2,
            color: theme.palette.neon.cyan,
            backgroundColor: 'rgba(0, 255, 255, 0.05)',
            fontWeight: 700,
            letterSpacing: '0.05em',
            px: 3,
            '&:hover': {
              borderColor: theme.palette.neon.cyan,
              borderWidth: 2,
              backgroundColor: 'rgba(0, 255, 255, 0.1)',
              boxShadow: `0 0 20px ${theme.palette.neon.cyan}`,
              transform: 'translateY(-2px)',
            },
            '&:disabled': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'rgba(255, 255, 255, 0.3)',
            }
          }}
        >
          {isResending ? (
            <CircularProgress size={24} sx={{ color: theme.palette.neon.cyan }} />
          ) : (
            '確認メールを再送信'
          )}
        </Button>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{
            borderRadius: 0,
            background: `linear-gradient(45deg, ${theme.palette.neon.lime} 0%, ${theme.palette.neon.cyan} 100%)`,
            boxShadow: `0 0 20px ${theme.palette.neon.lime}`,
            border: `2px solid ${theme.palette.neon.lime}`,
            fontWeight: 700,
            letterSpacing: '0.1em',
            px: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
              transition: 'left 0.5s ease',
            },
            '&:hover::before': {
              left: '100%',
            },
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 0 30px ${theme.palette.neon.lime}`,
            },
          }}
        >
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailVerificationModal;
